/**
 * Custom Visualizer Generation Module
 *
 * Handles the UI and logic for LLM-powered custom visualizer generation.
 * Provides a modal for entering descriptions and displays generation progress.
 */

import { SUPABASE_URL, isConfigured } from '../config/config.js';
import { visualizerLoader } from '../lib/visualizer-loader.js';
import { VisualizerRegistry } from '../visualizer.js';
import { thumbnailGenerator } from '../lib/thumbnail-generator.js';
import { thumbnailService } from '../lib/thumbnail-service.js';
import { lockBodyScroll, unlockBodyScroll } from '../lib/overflow-lock.js';

// Module state
let modal = null;
let isGenerating = false;
let onVisualizerCreated = null;

// Edit mode state
let editMode = null; // null | { mode: 'edit' | 'clone', visualizerId: string, visualizerName: string, existingCode: string }

/**
 * Generate and upload thumbnail/video previews for a newly created visualizer
 * Uses hybrid approach: blocks for static thumbnail, generates video in background
 * @param {string} vizName - The visualizer name
 * @returns {Promise<void>}
 */
async function generateVisualizerPreviews(vizName) {
  try {
    // Generate and upload static thumbnail (blocking - wait for this)
    console.log(`[CustomVisualizer] Generating static thumbnail for ${vizName}...`);
    const staticBlob = await thumbnailGenerator.generateStaticThumbnail(vizName);
    await thumbnailService.uploadToStorage(vizName, 'static', staticBlob);
    console.log(`[CustomVisualizer] Static thumbnail uploaded for ${vizName}`);

    // Generate video preview in background (non-blocking)
    thumbnailGenerator.generatePreviewVideo(vizName)
      .then(async (videoBlob) => {
        await thumbnailService.uploadToStorage(vizName, 'preview', videoBlob);
        console.log(`[CustomVisualizer] Video preview uploaded for ${vizName}`);
      })
      .catch(err => {
        console.warn(`[CustomVisualizer] Video preview generation failed for ${vizName}:`, err);
        // Non-fatal - visualizer still works, just no video preview
      });

  } catch (err) {
    console.warn(`[CustomVisualizer] Thumbnail generation failed for ${vizName}:`, err);
    // Non-fatal - visualizer still works, just no preview
  }
}

/**
 * Initialize the custom visualizer module
 * @param {Object} options
 * @param {Function} options.onCreated - Callback when a visualizer is created
 */
export function initCustomVisualizer(options = {}) {
  onVisualizerCreated = options.onCreated;
  createModal();
  console.log('[CustomVisualizer] Initialized');
}

/**
 * Create the generation modal
 */
function createModal() {
  // Check if modal already exists
  if (document.getElementById('customVisualizerModal')) {
    modal = document.getElementById('customVisualizerModal');
    return;
  }

  modal = document.createElement('div');
  modal.id = 'customVisualizerModal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-content custom-viz-modal">
      <div class="modal-header">
        <h3 id="customVizTitle">Create Custom Visualizer</h3>
        <button class="modal-close-btn" id="customVizCloseBtn">
          <i data-lucide="x"></i>
        </button>
      </div>

      <div class="modal-body" id="customVizBody">
        <!-- Input State -->
        <div id="customVizInput">
          <p class="custom-viz-description" id="customVizDescription">
            Describe your ideal visualizer in natural language. Our AI will generate
            a unique visualization based on your description.
          </p>

          <!-- Edit mode info banner -->
          <div class="custom-viz-edit-info" id="customVizEditInfo" style="display: none;">
            <i data-lucide="info"></i>
            <span id="customVizEditInfoText">Editing: Original Visualizer</span>
          </div>

          <div class="custom-viz-form">
            <label for="customVizPrompt" id="customVizPromptLabel">Describe your visualizer</label>
            <textarea
              id="customVizPrompt"
              placeholder="e.g., A cosmic galaxy with swirling stars that pulse and expand with the bass..."
              rows="4"
              maxlength="500"
            ></textarea>
            <div class="custom-viz-char-count">
              <span id="customVizCharCount">0</span>/500
            </div>
          </div>

          <div class="custom-viz-options" id="customVizOptionsSection">
            <label class="custom-viz-type-label">Visualizer Type</label>
            <div class="custom-viz-type-btns">
              <button class="type-btn active" data-type="2d" title="Simpler, more reliable">
                <i data-lucide="square"></i>
                2D Canvas
              </button>
              <button class="type-btn" data-type="webgpu" title="Advanced 3D graphics">
                <i data-lucide="box"></i>
                3D WebGPU
              </button>
            </div>
          </div>

          <div class="custom-viz-actions">
            <button class="btn-ghost" id="customVizCancelBtn">Cancel</button>
            <button class="btn-primary" id="customVizGenerateBtn" disabled>
              <i data-lucide="sparkles"></i>
              <span id="customVizGenerateBtnText">Generate</span>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div id="customVizLoading" class="custom-viz-loading" style="display: none;">
          <div class="loading-spinner"></div>
          <h4>Generating your visualizer...</h4>
          <p id="customVizLoadingText">This may take 10-20 seconds</p>
        </div>

        <!-- Success State -->
        <div id="customVizSuccess" class="custom-viz-success" style="display: none;">
          <div class="success-icon">
            <i data-lucide="check-circle"></i>
          </div>
          <h4>Visualizer Created!</h4>
          <p id="customVizSuccessName"></p>
          <button class="btn-primary" id="customVizUseBtn">
            <i data-lucide="play"></i>
            Use Now
          </button>
        </div>

        <!-- Error State -->
        <div id="customVizError" class="custom-viz-error" style="display: none;">
          <div class="error-icon">
            <i data-lucide="alert-circle"></i>
          </div>
          <h4>Generation Failed</h4>
          <p id="customVizErrorText"></p>
          <button class="btn-ghost" id="customVizRetryBtn">
            <i data-lucide="refresh-cw"></i>
            Try Again
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  setupModalEvents();

  // Initialize Lucide icons in the modal
  if (typeof lucide !== 'undefined') {
    lucide.createIcons({ nodes: [modal] });
  }
}

/**
 * Set up modal event listeners
 */
function setupModalEvents() {
  const closeBtn = modal.querySelector('#customVizCloseBtn');
  const cancelBtn = modal.querySelector('#customVizCancelBtn');
  const generateBtn = modal.querySelector('#customVizGenerateBtn');
  const promptInput = modal.querySelector('#customVizPrompt');
  const charCount = modal.querySelector('#customVizCharCount');
  const typeBtns = modal.querySelectorAll('.type-btn');
  const useBtn = modal.querySelector('#customVizUseBtn');
  const retryBtn = modal.querySelector('#customVizRetryBtn');

  // Close modal
  closeBtn?.addEventListener('click', hideModal);
  cancelBtn?.addEventListener('click', hideModal);

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      hideModal();
    }
  });

  // Close on ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) {
      hideModal();
    }
  });

  // Character count
  promptInput?.addEventListener('input', () => {
    const count = promptInput.value.length;
    charCount.textContent = count;

    // Enable generate button if enough characters
    generateBtn.disabled = count < 10;
  });

  // Type selection
  typeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      typeBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Generate button
  generateBtn?.addEventListener('click', handleGenerate);

  // Use button (after success)
  useBtn?.addEventListener('click', handleUseVisualizer);

  // Retry button
  retryBtn?.addEventListener('click', resetToInput);
}

/**
 * Show the modal in create mode
 */
export function showModal() {
  showModalInternal(null);
}

/**
 * Show the modal in edit mode
 * @param {Object} options
 * @param {string} options.mode - 'edit' (own visualizer) or 'clone' (copy someone else's)
 * @param {string} options.visualizerId - ID of the visualizer to edit/clone
 * @param {string} options.visualizerName - Display name of the visualizer
 * @param {string} options.existingCode - The existing visualizer code (optional if using server-side fetching)
 * @param {string} options.contextType - '2d' or 'webgpu'
 * @param {string} options.sourceType - 'user' | 'premium' | 'bundled' (for server-side fetching)
 * @param {string} options.sourceName - Source identifier for server-side fetching
 */
export function showEditModal(options) {
  showModalInternal(options);
}

/**
 * Internal function to show modal with optional edit mode
 * @param {Object|null} editOptions
 */
function showModalInternal(editOptions) {
  if (!modal) {
    createModal();
  }

  // Check if user is authenticated
  const supabase = visualizerLoader.getSupabaseClient();
  if (!supabase) {
    // Show auth required message
    alert('Please log in to create custom visualizers.');
    return;
  }

  // Set edit mode state
  editMode = editOptions;

  resetToInput();
  updateModalForMode();
  modal.classList.add('open');
  lockBodyScroll();

  // Focus the textarea
  const promptInput = modal.querySelector('#customVizPrompt');
  setTimeout(() => promptInput?.focus(), 100);
}

/**
 * Update modal UI based on current mode (create vs edit vs clone)
 */
function updateModalForMode() {
  const title = modal.querySelector('#customVizTitle');
  const description = modal.querySelector('#customVizDescription');
  const editInfo = modal.querySelector('#customVizEditInfo');
  const editInfoText = modal.querySelector('#customVizEditInfoText');
  const promptLabel = modal.querySelector('#customVizPromptLabel');
  const promptInput = modal.querySelector('#customVizPrompt');
  const generateBtnText = modal.querySelector('#customVizGenerateBtnText');
  const optionsSection = modal.querySelector('#customVizOptionsSection');
  const typeBtns = modal.querySelectorAll('.type-btn');

  if (editMode) {
    const isClone = editMode.mode === 'clone';

    // Update title
    title.textContent = isClone ? 'Clone & Edit Visualizer' : 'Edit Visualizer';

    // Update description
    description.textContent = isClone
      ? 'Describe how you want to modify this visualizer. A copy will be created with your changes.'
      : 'Describe the changes you want to make to this visualizer.';

    // Show edit info banner
    editInfo.style.display = 'flex';
    editInfoText.textContent = isClone
      ? `Cloning: ${editMode.visualizerName}`
      : `Editing: ${editMode.visualizerName}`;

    // Update prompt label and placeholder
    promptLabel.textContent = 'Describe your changes';
    promptInput.placeholder = 'e.g., Make the colors more vibrant, add a pulsing effect, change the shape to circles...';

    // Update button text
    generateBtnText.textContent = isClone ? 'Clone & Apply' : 'Apply Changes';

    // Hide type selector in edit mode (use existing context type)
    optionsSection.style.display = 'none';

    // Set the correct type button based on existing context type
    if (editMode.contextType) {
      typeBtns.forEach(btn => {
        btn.classList.toggle('active', btn.dataset.type === editMode.contextType);
      });
    }

    // Refresh icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons({ nodes: [editInfo] });
    }
  } else {
    // Create mode - reset to defaults
    title.textContent = 'Create Custom Visualizer';
    description.textContent = 'Describe your ideal visualizer in natural language. Our AI will generate a unique visualization based on your description.';
    editInfo.style.display = 'none';
    promptLabel.textContent = 'Describe your visualizer';
    promptInput.placeholder = 'e.g., A cosmic galaxy with swirling stars that pulse and expand with the bass...';
    generateBtnText.textContent = 'Generate';
    optionsSection.style.display = 'block';

    // Reset type buttons
    typeBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.type === '2d');
    });
  }
}

/**
 * Hide the modal
 */
export function hideModal() {
  if (isGenerating) return; // Don't close while generating

  modal?.classList.remove('open');
  unlockBodyScroll();

  // Clear edit mode state
  editMode = null;
}

/**
 * Reset modal to input state
 */
function resetToInput() {
  const inputDiv = modal.querySelector('#customVizInput');
  const loadingDiv = modal.querySelector('#customVizLoading');
  const successDiv = modal.querySelector('#customVizSuccess');
  const errorDiv = modal.querySelector('#customVizError');
  const promptInput = modal.querySelector('#customVizPrompt');
  const charCount = modal.querySelector('#customVizCharCount');
  const generateBtn = modal.querySelector('#customVizGenerateBtn');

  inputDiv.style.display = 'block';
  loadingDiv.style.display = 'none';
  successDiv.style.display = 'none';
  errorDiv.style.display = 'none';

  // Clear the prompt input
  if (promptInput) {
    promptInput.value = '';
  }
  if (charCount) {
    charCount.textContent = '0';
  }
  if (generateBtn) {
    generateBtn.disabled = true;
  }
}

/**
 * Show loading state
 */
function showLoading(message = 'This may take 10-20 seconds') {
  const inputDiv = modal.querySelector('#customVizInput');
  const loadingDiv = modal.querySelector('#customVizLoading');
  const loadingText = modal.querySelector('#customVizLoadingText');

  inputDiv.style.display = 'none';
  loadingDiv.style.display = 'flex';
  loadingText.textContent = message;
}

/**
 * Show success state
 */
function showSuccess(visualizerName) {
  const loadingDiv = modal.querySelector('#customVizLoading');
  const successDiv = modal.querySelector('#customVizSuccess');
  const successName = modal.querySelector('#customVizSuccessName');

  loadingDiv.style.display = 'none';
  successDiv.style.display = 'flex';
  successName.textContent = visualizerName;

  // Refresh Lucide icons
  if (typeof lucide !== 'undefined') {
    lucide.createIcons({ nodes: [successDiv] });
  }
}

/**
 * Show error state
 */
function showError(message) {
  const loadingDiv = modal.querySelector('#customVizLoading');
  const errorDiv = modal.querySelector('#customVizError');
  const errorText = modal.querySelector('#customVizErrorText');

  loadingDiv.style.display = 'none';
  errorDiv.style.display = 'flex';
  errorText.textContent = message;

  // Refresh Lucide icons
  if (typeof lucide !== 'undefined') {
    lucide.createIcons({ nodes: [errorDiv] });
  }
}

/**
 * Handle generate button click
 */
async function handleGenerate() {
  if (isGenerating) return;

  const promptInput = modal.querySelector('#customVizPrompt');
  const activeTypeBtn = modal.querySelector('.type-btn.active');

  const description = promptInput.value.trim();
  const contextType = editMode?.contextType || activeTypeBtn?.dataset.type || '2d';

  if (description.length < 10) {
    alert('Please enter a longer description (at least 10 characters).');
    return;
  }

  isGenerating = true;

  // Show appropriate loading message
  const isEdit = editMode !== null;
  const isClone = editMode?.mode === 'clone';
  if (isClone) {
    showLoading('Cloning and applying your changes...');
  } else if (isEdit) {
    showLoading('Applying your changes...');
  } else {
    showLoading();
  }

  try {
    let result;

    if (editMode) {
      // Edit or clone mode - prefer server-side fetching if source info is available
      result = await editVisualizer({
        editPrompt: description,
        contextType,
        mode: editMode.mode,
        visualizerId: editMode.visualizerId,
        existingCode: editMode.existingCode,
        sourceType: editMode.sourceType,
        sourceName: editMode.sourceName
      });
    } else {
      // Create mode
      result = await generateVisualizer(description, contextType);
    }

    if (result.success) {
      // Clear cache to refresh visualizer list
      visualizerLoader.clearUserVisualizersCache();
      VisualizerRegistry.clearCache();

      // Generate and upload thumbnail preview (blocks for static, video in background)
      showLoading('Generating preview thumbnail...');
      await generateVisualizerPreviews(result.visualizer.name);

      // Store the created visualizer info for use button
      modal.dataset.createdVizName = result.visualizer.name;
      modal.dataset.createdVizId = result.visualizer.id;

      const successMessage = isClone
        ? `Cloned: ${result.visualizer.displayName}`
        : isEdit
        ? `Updated: ${result.visualizer.displayName}`
        : result.visualizer.displayName;

      showSuccess(successMessage);

      // Notify parent
      if (onVisualizerCreated) {
        onVisualizerCreated(result.visualizer);
      }
    } else {
      showError(result.message || 'Failed to generate visualizer');
    }
  } catch (error) {
    console.error('[CustomVisualizer] Generation error:', error);
    showError(error.message || 'An unexpected error occurred');
  } finally {
    isGenerating = false;
  }
}

/**
 * Call the generate-visualizer Edge Function
 */
async function generateVisualizer(description, contextType) {
  if (!isConfigured()) {
    throw new Error('Supabase is not configured');
  }

  const supabase = visualizerLoader.getSupabaseClient();
  if (!supabase) {
    throw new Error('Not authenticated');
  }

  // Get the auth token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Please log in to create custom visualizers');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-visualizer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({
      description,
      contextType
    })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Generation failed');
  }

  return data;
}

/**
 * Call the generate-visualizer Edge Function in edit mode
 * Supports both client-side code passing and server-side fetching
 * @param {Object} options
 * @param {string} options.editPrompt - Description of changes to make
 * @param {string} options.contextType - '2d' or 'webgpu'
 * @param {string} options.mode - 'edit' or 'clone'
 * @param {string} options.visualizerId - ID of the visualizer being edited (for edit mode)
 * @param {string} [options.existingCode] - The existing visualizer code (client-side)
 * @param {string} [options.sourceType] - 'user' | 'premium' | 'bundled' (for server-side fetching)
 * @param {string} [options.sourceName] - Source identifier for server-side fetching
 */
async function editVisualizer(options) {
  const { editPrompt, contextType, mode, visualizerId, existingCode, sourceType, sourceName } = options;

  if (!isConfigured()) {
    throw new Error('Supabase is not configured');
  }

  const supabase = visualizerLoader.getSupabaseClient();
  if (!supabase) {
    throw new Error('Not authenticated');
  }

  // Get the auth token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Please log in to edit visualizers');
  }

  // Build request body - prefer server-side fetching if source info is provided
  const requestBody = {
    description: editPrompt,
    contextType,
    mode: mode, // 'edit' or 'clone'
    visualizerId: mode === 'edit' ? visualizerId : undefined
  };

  // Use server-side fetching if source info is provided, otherwise fall back to client-side code
  if (sourceType && sourceName) {
    requestBody.sourceType = sourceType;
    requestBody.sourceName = sourceName;
  } else if (existingCode) {
    requestBody.existingCode = existingCode;
  } else {
    throw new Error('Either existingCode or source info (sourceType + sourceName) is required');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-visualizer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify(requestBody)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Edit failed');
  }

  return data;
}

/**
 * Fetch visualizer code from storage for editing
 * For bundled visualizers, returns null code to indicate server-side fetching should be used
 * @param {string} visualizerId - The visualizer ID or name
 * @param {string} source - 'user', 'bundled', or 'premium'
 * @returns {Promise<{code: string|null, contextType: string, useServerFetch: boolean}>}
 */
export async function fetchVisualizerCode(visualizerId, source) {
  const supabase = visualizerLoader.getSupabaseClient();

  if (source === 'user') {
    // Fetch from user-visualizers bucket
    const userVizs = await visualizerLoader.getUserVisualizers();
    const viz = userVizs.find(v => v.id === visualizerId || v.name === visualizerId);

    if (!viz) {
      throw new Error('Visualizer not found');
    }

    const { data, error } = await supabase.storage
      .from('user-visualizers')
      .download(viz.storage_path);

    if (error) {
      throw new Error(`Failed to fetch visualizer code: ${error.message}`);
    }

    const code = await data.text();
    return { code, contextType: viz.context_type || '2d', useServerFetch: false };
  } else if (source === 'premium') {
    // Fetch from premium-visualizers bucket
    const manifest = visualizerLoader.getPremiumManifest();
    const vizInfo = manifest[visualizerId];

    if (!vizInfo) {
      throw new Error('Premium visualizer not found');
    }

    const { data, error } = await supabase.storage
      .from('premium-visualizers')
      .download(vizInfo.filename);

    if (error) {
      throw new Error(`Failed to fetch visualizer code: ${error.message}`);
    }

    const code = await data.text();
    return { code, contextType: vizInfo.contextType || '2d', useServerFetch: false };
  } else if (source === 'bundled') {
    // Bundled visualizers - use server-side fetching
    // The server has embedded copies of bundled visualizers
    const vizList = VisualizerRegistry.getListSync();
    const viz = vizList.find(v => v.name === visualizerId);
    const contextType = viz?.contextType || '2d';

    return {
      code: null, // Server will fetch this
      contextType,
      useServerFetch: true
    };
  } else {
    throw new Error(`Unknown source type: ${source}`);
  }
}

/**
 * Handle "Use Now" button click
 */
function handleUseVisualizer() {
  const vizName = modal.dataset.createdVizName;

  hideModal();

  // Dispatch event to switch to the new visualizer
  document.dispatchEvent(new CustomEvent('selectVisualizer', {
    detail: { name: vizName, source: 'user' }
  }));
}

/**
 * Delete a user visualizer
 */
export async function deleteVisualizer(visualizerId) {
  if (!isConfigured()) {
    throw new Error('Supabase is not configured');
  }

  const supabase = visualizerLoader.getSupabaseClient();
  if (!supabase) {
    throw new Error('Not authenticated');
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Please log in to delete visualizers');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/delete-visualizer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({
      visualizerId
    })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Delete failed');
  }

  // Clear cache
  visualizerLoader.clearUserVisualizersCache();
  VisualizerRegistry.clearCache();

  return data;
}

/**
 * Get user visualizers list
 */
export async function getUserVisualizers() {
  return visualizerLoader.getUserVisualizers();
}
